import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import ClearIcon from "@material-ui/icons/Clear";
import CircularProgress from "@material-ui/core/CircularProgress";
import "./InvoiceSearchToolbar.css";

@inject("invoiceStore", "profileStore")
@observer
class InvoiceSearchToolbar extends Component {
    constructor(props) {
        super(props);
        this.state = {
            localSearchValue: this.getStoreSearchTerm(),
            lastKnownStoreTerm: this.getStoreSearchTerm(),
            isLoadingAll: false
        };
        this.debounceTimer = null;
    }

    componentDidUpdate() {
        const currentStoreTerm = this.getStoreSearchTerm();
        const { lastKnownStoreTerm, localSearchValue } = this.state;
        
        if (currentStoreTerm !== lastKnownStoreTerm && currentStoreTerm !== localSearchValue) {
            this.setState({ 
                localSearchValue: currentStoreTerm,
                lastKnownStoreTerm: currentStoreTerm
            });
        }
    }

    componentWillUnmount() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
    }

    getStoreSearchTerm = () => {
        const { invoiceStore, type } = this.props;
        return type === "outgoing" 
            ? invoiceStore.outgoingSearchTerm 
            : invoiceStore.incomingSearchTerm;
    };

    getIsSearching = () => {
        const { invoiceStore, type } = this.props;
        return type === "outgoing"
            ? invoiceStore.isSearchingOutgoing
            : invoiceStore.isSearchingIncoming;
    };

    handleSearchChange = (e) => {
        const value = e.target.value;
        this.setState({ localSearchValue: value });

        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }

        this.debounceTimer = setTimeout(() => {
            const { invoiceStore, type, onSearch } = this.props;
            if (type === "outgoing") {
                invoiceStore.setOutgoingSearchTerm(value);
            } else {
                invoiceStore.setIncomingSearchTerm(value);
            }
            this.setState({ lastKnownStoreTerm: value });
            if (onSearch) {
                onSearch(value);
            }
        }, 500);
    };

    handleClearSearch = () => {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
        this.setState({ localSearchValue: "", lastKnownStoreTerm: "" });
        const { invoiceStore, type, onSearch } = this.props;
        if (type === "outgoing") {
            invoiceStore.clearOutgoingSearch();
        } else {
            invoiceStore.clearIncomingSearch();
        }
        if (onSearch) {
            onSearch("");
        }
    };

    handleLoadAllClick = async () => {
        const { onLoadAll } = this.props;
        if (onLoadAll) {
            this.setState({ isLoadingAll: true });
            try {
                await onLoadAll();
            } finally {
                this.setState({ isLoadingAll: false });
            }
        }
    };

    render() {
        const { invoiceStore, type, onLoadAll } = this.props;
        const { localSearchValue, isLoadingAll } = this.state;
        const storeSearchTerm = this.getStoreSearchTerm();
        const isSearching = this.getIsSearching();

        const isOutgoing = type === "outgoing";
        const allInvoices = isOutgoing ? invoiceStore.outgoingInvoices : invoiceStore.incomingInvoices;
        const showMore = isOutgoing ? invoiceStore.showMoreOutgoing : invoiceStore.showMoreIncoming;
        const hasSearch = storeSearchTerm.length > 0;
        const resultCount = allInvoices.length;

        return (
            <div className="invoice-search-toolbar">
                <div className="invoice-search-left">
                    <div className="invoice-search-input-wrapper">
                        <TextField
                            value={localSearchValue}
                            onChange={this.handleSearchChange}
                            placeholder="Search invoices..."
                            variant="outlined"
                            size="small"
                            className="invoice-search-input"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {isSearching ? (
                                            <CircularProgress size={18} className="invoice-search-spinner" />
                                        ) : localSearchValue ? (
                                            <IconButton
                                                size="small"
                                                onClick={this.handleClearSearch}
                                                className="invoice-search-clear-btn"
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        ) : null}
                                    </InputAdornment>
                                )
                            }}
                        />
                    </div>

                    {hasSearch && !isSearching && (
                        <div className="invoice-search-results">
                            <strong>{resultCount}</strong> invoice{resultCount !== 1 ? 's' : ''} found for "<span className="invoice-search-term">{storeSearchTerm}</span>"
                        </div>
                    )}

                    {!hasSearch && (
                        <div className="invoice-load-info-bar">
                            <span className="invoice-load-badge">{resultCount}</span>
                            <span className="invoice-load-text">invoices loaded</span>
                            {showMore && onLoadAll && (
                                <>
                                    <span className="invoice-load-divider"></span>
                                    <button
                                        className={`invoice-load-all-btn${isLoadingAll ? ' loading' : ''}`}
                                        onClick={this.handleLoadAllClick}
                                        disabled={isLoadingAll}
                                    >
                                        {isLoadingAll ? (
                                            <>
                                                <span className="invoice-css-spinner"></span>
                                                <span>Loading...</span>
                                            </>
                                        ) : (
                                            <>
                                                <span style={{ fontSize: 16, lineHeight: 1 }}>&#x2B07;</span>
                                                <span>Load All</span>
                                            </>
                                        )}
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default InvoiceSearchToolbar;
